import React, { useEffect, useId, useRef, useState } from 'react';
import { householdAPI } from '../../services/api';
import { apiErrorMessage } from './propertyRegistry';

export default function PropertyHouseholdPicker({ tenantSlug, value, initialHousehold, onChange, required = false }) {
  const id = useId();
  const sequence = useRef(0);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(
    initialHousehold && String(initialHousehold.id) === String(value) ? initialHousehold : null
  );
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (!value) {
      setSelected(null);
      return;
    }

    if (initialHousehold && String(initialHousehold.id) === String(value)) {
      setSelected(initialHousehold);
      return;
    }

    if (selected && String(selected.id) === String(value) && selected.head_name) {
      return;
    }

    let isMounted = true;
    householdAPI.getById(tenantSlug, value)
      .then(response => {
        if (!isMounted) return;
        const h = response.data?.household;
        if (h) {
          setSelected(h);
        } else {
          setSelected({ id: value, household_uid: `कुटुंब क्र. ${value}` });
        }
      })
      .catch(() => {
        if (!isMounted) return;
        setSelected({ id: value, household_uid: `कुटुंब क्र. ${value}` });
      });

    return () => { isMounted = false; };
  }, [value, initialHousehold, tenantSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selected || query.trim().length < 2) {
      setItems([]);
      setSearched(false);
      return undefined;
    }
    const current = ++sequence.current;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const response = await householdAPI.getAll(tenantSlug, { search: query.trim(), page: 1, limit: 12, sort_by: 'head_name', sort_order: 'ASC' });
        if (current !== sequence.current) return;
        setItems(response.data.households || []);
        setSearched(true);
      } catch (requestError) {
        if (current !== sequence.current) return;
        setError(apiErrorMessage(requestError, 'कुटुंब शोध उपलब्ध नाही.'));
        setItems([]);
        setSearched(true);
      } finally {
        if (current === sequence.current) setLoading(false);
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [query, selected, tenantSlug]);

  const choose = household => {
    setSelected(household);
    setQuery('');
    setItems([]);
    setSearched(false);
    onChange(String(household.id));
  };

  return (
    <div className="property-party-picker">
      <label htmlFor={`${id}-household`}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          संबंधित कुटुंब / नमुना ३३ (Associated Household)
          {required ? <span className="req-star" aria-hidden="true" style={{ color: '#dc2626' }}>*</span> : <span className="opt-badge" style={{ fontWeight: 400, color: '#64748b' }}>(ऐच्छिक)</span>}
        </span>
      </label>
      {selected ? (
        <div
          className="property-party-selected"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0.75rem 1rem',
            backgroundColor: '#f8fafc',
            border: '1px solid #cbd5e1',
            borderRadius: '8px',
            gap: '1rem',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.95rem' }}>
                🏠 {selected.household_uid || `कुटुंब क्र. ${selected.id}`}
              </span>
              {selected.head_name && (
                <span
                  style={{
                    color: '#0369a1',
                    fontWeight: 600,
                    backgroundColor: '#e0f2fe',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '0.82rem',
                  }}
                >
                  प्रमुख: {selected.head_name}
                </span>
              )}
            </div>
            <div style={{ color: '#64748b', fontSize: '0.82rem', marginTop: '0.3rem' }}>
              {selected.address ? `पत्ता: ${selected.address}` : ''}
              {selected.ward_number ? ` · प्रभाग: ${selected.ward_number}` : ''}
              {selected.total_members ? ` · एकूण सदस्य: ${selected.total_members}` : ''}
            </div>
          </div>
          <button
            type="button"
            className="property-icon-button"
            onClick={() => { setSelected(null); onChange(''); }}
            aria-label="कुटुंब निवड काढा"
            title="कुटुंब निवड काढा"
          >
            ×
          </button>
        </div>
      ) : (
        <div className="property-combobox-wrap">
          <input
            id={`${id}-household`}
            type="search"
            role="combobox"
            aria-expanded={searched || Boolean(error)}
            aria-controls={`${id}-household-results`}
            value={query}
            onChange={event => setQuery(event.target.value)}
            required={required}
            placeholder="कुटुंब UID, प्रमुखाचे नाव किंवा पत्ता शोधा..."
            autoComplete="off"
          />
          <small className="property-field-hint">
            {required ? 'निवासी मालमत्तेसाठी कुटुंब निवडणे आवश्यक आहे (किमान २ अक्षरे टाइप करा)' : 'किमान २ अक्षरे टाइप करा'}
          </small>
          {loading && <span className="property-inline-loader" role="status">शोधत आहे…</span>}
          {(searched || error) && (
            <div id={`${id}-household-results`} className="property-combobox-results" role="listbox">
              {error && <p className="property-combobox-message" role="alert">{error}</p>}
              {!error && !loading && items.length === 0 && (
                <p className="property-combobox-message">जुळणारे कुटुंब सापडले नाही.</p>
              )}
              {items.map(household => (
                <button
                  type="button"
                  role="option"
                  aria-selected="false"
                  key={household.id}
                  onClick={() => choose(household)}
                  style={{ textAlign: 'left', padding: '0.65rem 0.85rem', borderBottom: '1px solid #f1f5f9' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                    <strong>🏠 {household.household_uid}</strong>
                    <span style={{ color: '#0369a1', fontWeight: 600 }}>{household.head_name || 'नाव उपलब्ध नाही'}</span>
                  </div>
                  <small style={{ color: '#64748b', display: 'block', marginTop: '3px' }}>
                    {household.address || 'पत्ता नोंद नाही'} {household.ward_number ? `· प्रभाग ${household.ward_number}` : ''} {household.total_members ? `· सदस्य: ${household.total_members}` : ''}
                  </small>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
